import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { mcpService } from '../../../../../../Services/mcpService';
import { RootState, useAppSelector } from '../../../../../../store';

interface McpConfig {
    identifier: string;
    title: string;
    serverUrl: string;
}

interface McpTool {
    identifier: string;
    name: string;
    description: string;
    inputSchema: any;
    config: {
        identifier: string;
        title: string;
    };
}

export interface UseMcpTesterParams {
    preselectedConfigIdentifier?: string;
    resetSignal?: any;
}

export function useMcpTester({ preselectedConfigIdentifier, resetSignal }: UseMcpTesterParams) {
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);

    const [mcpConfigs, setMcpConfigs] = useState<McpConfig[]>([]);
    const [mcpTools, setMcpTools] = useState<McpTool[]>([]);
    const [selectedConfig, setSelectedConfig] = useState<string>('');
    const [selectedTool, setSelectedTool] = useState<string>('');
    const [toolArguments, setToolArguments] = useState<string>('{}');
    const [result, setResult] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [canExecute, setCanExecute] = useState(false);

    useEffect(() => {
        loadMcpConfigs();
        loadMcpTools();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetSignal, preselectedConfigIdentifier]);

    useEffect(() => {
        if (preselectedConfigIdentifier && mcpConfigs.some((c) => c.identifier === preselectedConfigIdentifier)) {
            setSelectedConfig(preselectedConfigIdentifier);
            setSelectedTool('');
        }
    }, [preselectedConfigIdentifier, mcpConfigs]);

    useEffect(() => {
        if (resetSignal !== undefined) {
            setResult(null);
            setToolArguments('{}');
            setSelectedTool('');
        }
    }, [resetSignal]);

    useEffect(() => {
        if (!selectedConfig) {
            setSelectedTool('');
            setToolArguments('{}');
            setResult(null);
        }
    }, [selectedConfig]);

    const loadMcpConfigs = async () => {
        try {
            const response = await mcpService.getConfigs();
            setMcpConfigs((response.data && response.data.data) || []);
        } catch (error) {
            console.error('Failed to load MCP configs:', error);
        }
    };

    const loadMcpTools = async () => {
        try {
            if (!preselectedConfigIdentifier) return;
            const response = await mcpService.getTools(preselectedConfigIdentifier || selectedConfig);
            const totalItems = response.data?.total || 0;
            if (totalItems > response.data.data.length) {
                const pages = Math.ceil(totalItems / response.data.data.length);
                let allTools = response.data.data;
                for (let page = 2; page <= pages; page++) {
                    const pagedResponse = await mcpService.getTools(
                        preselectedConfigIdentifier || selectedConfig,
                        page,
                        response.data.data.length,
                    );
                    allTools = allTools.concat(pagedResponse.data.data);
                }
                setMcpTools(allTools);
                return;
            }
        } catch (error) {
            console.error('Failed to load MCP tools:', error);
        }
    };

    const executeTool = async () => {
        const loadingToastId = toast.loading(i18n.translate({ id: 'testing.executing', sourceName: 'MCP' }));
        if (!selectedTool) {
            toast.error(i18n.translate({ id: 'tester.error.noTool', sourceName: 'MCP' }));
            return;
        }

        let parsedArguments;
        try {
            parsedArguments = JSON.parse(toolArguments);
        } catch (error) {
            toast.error(i18n.translate({ id: 'tester.error.invalidJson', sourceName: 'MCP' }));
            return;
        }

        setIsLoading(true);

        try {
            const response = await mcpService.executeTool(selectedTool, parsedArguments);
            toast.dismiss(loadingToastId);

            setResult(response.data);
            if (response.data.success) {
                toast.success(i18n.translate({ id: 'tester.success', sourceName: 'MCP' }));
            } else {
                toast.error(i18n.translate({ id: 'tester.failed', sourceName: 'MCP' }));
            }
        } catch (error: any) {
            setResult({ error: error?.message ?? i18n.translate({ id: 'tester.unknownError', sourceName: 'MCP' }) });
            toast.error(i18n.translate({ id: 'tester.failed', sourceName: 'MCP' }));
        } finally {
            setIsLoading(false);
        }
    };

    const selectedToolData = useMemo(
        () => mcpTools.find((tool) => tool.identifier === selectedTool),
        [mcpTools, selectedTool],
    );

    const clearResults = () => {
        setResult(null);
    };

    return {
        // data
        mcpConfigs,
        mcpTools,
        selectedConfig,
        selectedTool,
        toolArguments,
        result,
        isLoading,
        canExecute,
        selectedToolData,
        // actions
        setSelectedConfig,
        setSelectedTool,
        setToolArguments,
        executeTool,
        clearResults,
        setCanExecute,
    } as const;
}

export default useMcpTester;
